import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PlaceIcon from "@mui/icons-material/Place";
import PublicIcon from "@mui/icons-material/Public";
import DirectionsBoatFilledIcon from "@mui/icons-material/DirectionsBoatFilled";
import { GoogleMap, OverlayView, Polyline, useLoadScript } from "@react-google-maps/api";
import Card from "@plannel/components/dashboard/DashboardCard";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
 * DW 대시보드 공용 월드맵 패널 — 좌표를 가진 엔티티를 마커로 표시.
 * 도메인 무관: points 는 { name, lat, lng, amt } 형태면 무엇이든 됨(거래처/거점 등).
 * network 를 주면 그 아래에 연결망(거점 노드 + 선)을 배경 레이어로 깐다.
 *
 * ⚠ API 키는 반드시 env 로 주입한다. 키를 소스에 하드코딩하지 말 것
 *   (pages/dashboard_temp 의 목업들이 하드코딩한 나쁜 예 — 그대로 베끼지 말 것).
 */
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY; /* eslint-disable-line */

// 월드맵 스타일 — 라벨/도로/POI 숨김, 육지 흰색·바다 옅은 블루그레이, 국경선만 옅게
const WORLD_MAP_STYLES = [
  { featureType: "all", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "geometry.fill", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "landscape.natural", elementType: "geometry.stroke", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry.fill", stylers: [{ color: "#E6EDF5" }] },
  { featureType: "water", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ visibility: "on" }, { color: "#C2CCDA" }, { weight: 1 }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "geometry.stroke", stylers: [{ visibility: "off" }] },
];

// 폴백 뷰(좌표 있는 항목이 하나도 없을 때만) + fit 후 과도 확대 상한
const MAP_INITIAL = { center: { lat: 25, lng: 15 }, zoom: 2 };
const MAP_MAX_FIT_ZOOM = 6;   // 단일 도시 밀집 시 지나친 확대 방지

// 마커 크기(px) — 값 대비로 보간. 면적이 값에 비례하도록 sqrt 스케일(지름 선형은 큰 값이 과장된다).
const PIN_MIN = 18;
const PIN_MAX = 34;
const PIN_ACTIVE_BONUS = 8;   // hover/선택 시 추가 크기

// 배경 연결망 렌더 상한 — 오버레이 개수가 곧 팬/줌 비용이다.
//   선(Polyline)은 가볍지만 **화살표 심볼**이 비싸고, 거점(OverlayView)은 DOM 노드라 더 비싸다.
//   백엔드도 링크를 자르지만(BOD_LINK_LIMIT), 프런트는 다른 소스의 network 를 받을 수도 있어 여기서도 막는다.
const NET_LINK_MAX = 300;      // 초과분은 그리지 않음
const NET_ARROW_MAX = 120;     // 이 이상이면 방향 화살표 생략(선만) — 심볼 렌더가 가장 무겁다
const NET_NODE_MAX = 150;      // 이 이상이면 거점 마커 생략(선만으로도 망 모양은 읽힌다)

/**
 * @param items       [{ name, lat, lng, amt, qty }] — 순서가 마커 색(ColorUtil.colorSet) 인덱스,
 *                    마커 크기는 amt 비율(최대값 기준)
 * @param currencyCd  금액 포맷용 통화
 * @param highlighted 외부(테이블 등)에서 선택된 name → 해당 마커로 pan + 강조
 * @param onReset     리셋 버튼 클릭 시(선택 해제 등)
 * @param onItemClick 마커 클릭 시(드릴스루 등). 없으면 클릭 비활성
 * @param network     { nodes: [{ name, lat, lng }], links: [{ fromLat, fromLng, toLat, toLng, fromName, toName }] }
 *                    배경 연결망(BOD 등). 마커보다 아래에 옅게 깔린다
 * @param title       주면 지도 위에 카드 제목이 붙는다. **안 주면 기존처럼 지도만**(하위호환 — Sales Status)
 * @param onDrill     제목을 드릴 링크로(§9-1). 지도는 마커·리셋 버튼이 클릭 요소라 **제목만** 링크가 된다
 * @param drillTitle  링크 툴팁(대상 리포트 이름)  @param onDrillHover 제목 hover 상태(호출측이 카드에 하이라이트)
 */
const WorldMapPanel = ({ items = [], currencyCd, highlighted, onReset, onItemClick, network, sx,
  title, onDrill, drillTitle, onDrillHover }) => {
  const theme = useTheme();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  const mapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [nodeHover, setNodeHover] = useState(null);   // hover 중인 거점 **객체**(연결선 매칭에 키가 필요)
  const active = (name) => hover === name || highlighted === name;

  // 거점 키 — id 우선. 이름은 중복될 수 있어 선 매칭 기준으로 못 쓴다.
  const nodeKey = (n) => n?.id ?? n?.name;
  const hoverKey = nodeKey(nodeHover);

  // 좌표 있는 항목만 마커로(색은 순위 기준으로 테이블/그리드와 일치)
  const points = useMemo(() => items
    .map((c, i) => ({ ...c, colorIdx: i }))
    .filter((c) => c.lat != null && c.lng != null && !(c.lat === 0 && c.lng === 0)),
  [items]);

  // 마커 크기 스케일 — 최대 금액 기준. 값이 하나뿐이거나 전부 0 이면 전부 최대 크기.
  const pinSize = useCallback((amt) => {
    const max = Math.max(...points.map((p) => Math.abs(p.amt ?? 0)), 0);
    if (!(max > 0)) return PIN_MAX;
    return PIN_MIN + (PIN_MAX - PIN_MIN) * Math.sqrt(Math.abs(amt ?? 0) / max);
  }, [points]);

  // 배경 연결망 — 좌표 있는 링크/노드만. 상한 초과분은 잘라서 오버레이 개수를 묶어 둔다.
  const netLinks = useMemo(() => (network?.links ?? [])
    .filter((l) => l.fromLat != null && l.fromLng != null && l.toLat != null && l.toLng != null)
    .slice(0, NET_LINK_MAX),
  [network]);
  const allNodes = useMemo(() => (network?.nodes ?? [])
    .filter((n) => n.lat != null && n.lng != null),
  [network]);
  // 노드는 뷰 맞춤(fitBounds)에는 전부 쓰고, **그리는 건** 상한 이하일 때만
  const netNodes = allNodes.length <= NET_NODE_MAX ? allNodes : [];

  // 연결망 선 — 기본은 옅은 회색조(마커 팔레트와 경쟁하지 않게), 거점 hover 시 그 거점에 걸린 선만 primary.
  //   geodesic 으로 대권 곡선 + 진행 방향 화살표.
  const arrow = useCallback((color, opacity, scale) => (
    isLoaded && window.google?.maps
      ? [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale,
        strokeColor: color, strokeOpacity: opacity, fillOpacity: 0 }, offset: "60%" }]
      : undefined
  ), [isLoaded]);

  const linkOptions = useMemo(() => ({
    geodesic: true,
    strokeColor: theme.palette.text.secondary,
    strokeOpacity: 0.35,
    strokeWeight: 1.2,
    clickable: false,
    zIndex: 1,
    // 화살표는 심볼 렌더가 무거워 링크가 많으면 생략(선만) — 강조 선은 몇 개뿐이라 항상 그린다
    icons: netLinks.length <= NET_ARROW_MAX ? arrow(theme.palette.text.secondary, 0.5, 2.2) : undefined,
  }), [theme, netLinks, arrow]);

  const linkOptionsActive = useMemo(() => ({
    geodesic: true,
    strokeColor: theme.palette.primary.main,
    strokeOpacity: 0.9,
    strokeWeight: 2.4,
    clickable: false,
    zIndex: 3,   // 회색 선 위로 올라와야 교차 구간에서 가려지지 않는다
    icons: arrow(theme.palette.primary.main, 1, 2.6),
  }), [theme, arrow]);

  // 이 선이 hover 중인 거점에 걸려 있나(양끝 중 하나)
  const linkTouchesHover = useCallback((l) => (
    hoverKey != null && ((l.fromId ?? l.fromName) === hoverKey || (l.toId ?? l.toName) === hoverKey)
  ), [hoverKey]);

  // 마커 전체가 보이도록 초기/리셋 뷰를 자동 맞춤(예: 전부 한국이면 한국으로 포커스).
  // 좌표가 하나도 없으면 폴백 월드뷰. 밀집 시 MAP_MAX_FIT_ZOOM 로 과도 확대만 방지.
  const fitToPoints = useCallback((map) => {
    if (!map || !window.google?.maps) return;
    // 연결망 거점도 화면 안에 들어와야 한다(거래처만 기준으로 맞추면 상류 거점이 잘린다).
    //   마커를 생략한 거점(상한 초과)도 뷰 맞춤에는 포함 — 선은 그려지므로 화면 밖이면 잘린 것처럼 보인다.
    const all = [...points, ...allNodes];
    if (all.length === 0) { map.setCenter(MAP_INITIAL.center); map.setZoom(MAP_INITIAL.zoom); return; }
    const bounds = new window.google.maps.LatLngBounds();
    all.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 56);
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > MAP_MAX_FIT_ZOOM) map.setZoom(MAP_MAX_FIT_ZOOM);
    });
  }, [points, allNodes]);

  // 데이터 갱신 시 뷰 재맞춤
  useEffect(() => { if (mapRef.current) fitToPoints(mapRef.current); }, [fitToPoints]);

  // 외부 선택 → 해당 마커로 이동(하이라이트)
  useEffect(() => {
    if (!highlighted || !mapRef.current) return;
    const c = points.find((p) => p.name === highlighted);
    if (c) {
      mapRef.current.panTo({ lat: c.lat, lng: c.lng });
      if (mapRef.current.getZoom() < 4) mapRef.current.setZoom(4);
    }
  }, [highlighted, points]);

  const resetView = () => {
    if (mapRef.current) fitToPoints(mapRef.current);
    onReset?.();
  };

  return (
    /* 제목이 없으면 예전 그대로 지도가 카드를 꽉 채운다. 제목이 있으면 헤더 + 지도 2단(flex column) */
    <Card sx={{ flex: 2, minHeight: 0, p: 0, overflow: "hidden",
      display: "flex", flexDirection: "column", ...sx }}>
      {title && (
        <Box sx={{ px: 1.25, pt: 1 }}>
          <DashboardCardHeader title={title} dense
            onDrill={onDrill} drillTitle={drillTitle} onDrillHover={onDrillHover} />
        </Box>
      )}
      {/* 리셋 버튼의 기준 박스 = 지도 영역(헤더 아래) */}
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
      {isLoaded && (
        <IconButton size="small" onClick={resetView} aria-label="reset map view"
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 2, bgcolor: "background.paper",
            border: 1, borderColor: "divider", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
            "&:hover": { bgcolor: "background.paper" } }}>
          <PublicIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        </IconButton>
      )}
      {!isLoaded ? (
        <Box sx={{ width: "100%", height: "100%" }} />
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%", backgroundColor: "#FFFFFF" }}
          center={MAP_INITIAL.center}
          zoom={MAP_INITIAL.zoom}
          onLoad={(m) => { mapRef.current = m; fitToPoints(m); }}
          options={{ disableDefaultUI: true, styles: WORLD_MAP_STYLES, backgroundColor: "#FFFFFF", minZoom: 2 }}
        >
          {/* ── 배경 레이어: 연결망(선 + 거점) — 마커보다 아래(zIndex) ── */}
          {netLinks.map((l, i) => (
            <Polyline
              key={`${l.fromName}-${l.toName}-${i}`}
              path={[{ lat: l.fromLat, lng: l.fromLng }, { lat: l.toLat, lng: l.toLng }]}
              options={linkTouchesHover(l) ? linkOptionsActive : linkOptions}
            />
          ))}
          {netNodes.map((n) => {
            const on = hoverKey != null && nodeKey(n) === hoverKey;
            return (
              <OverlayView key={`node-${nodeKey(n)}`} position={{ lat: n.lat, lng: n.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                {/* 거점 = 흰 원형 칩 + 선박 아이콘(지도 POI 관용구).
                    트럭이 아니라 배인 이유: 이 망은 국가를 넘는 **글로벌 네트워크**라 육상 운송 기호가 맞지 않는다.
                    거래처는 컬러 물방울 핀, 거점은 무채색 원형 칩 — **형태와 채도 둘 다** 달라서
                    색을 못 읽는 상황에서도 구분된다. hover 하면 칩과 **그 거점에 걸린 선**이 함께 primary 로 뜬다. */}
                <Box onMouseEnter={() => setNodeHover(n)} onMouseLeave={() => setNodeHover(null)}
                  sx={{ transform: "translate(-50%, -50%)", position: "relative", cursor: "default" }}>
                  <Box sx={{
                    width: on ? 24 : 20,
                    height: on ? 24 : 20,
                    borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "background.paper",
                    border: "1.5px solid",
                    borderColor: on ? "primary.main" : alpha(theme.palette.text.secondary, 0.45),
                    boxShadow: on ? "0 2px 7px rgba(0,0,0,0.28)" : "0 1px 4px rgba(0,0,0,0.18)",
                    transition: "all 0.15s",
                  }}>
                    {/* Filled 변형 — 12px 에서 외곽선형은 획이 흐려 실루엣이 또렷한 쪽을 쓴다 */}
                    <DirectionsBoatFilledIcon sx={{
                      fontSize: on ? 14 : 12,
                      color: on ? "primary.main" : alpha(theme.palette.text.secondary, 0.8),
                      transition: "all 0.15s",
                    }} />
                  </Box>
                  {on && (
                    <Box sx={{
                      position: "absolute", bottom: "130%", left: "50%", transform: "translateX(-50%)",
                      whiteSpace: "nowrap", bgcolor: "rgba(255,255,255,0.96)", border: 1, borderColor: "divider",
                      borderRadius: 1, px: 0.75, py: 0.25, boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    }}>
                      <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{n.name}</Typography>
                    </Box>
                  )}
                </Box>
              </OverlayView>
            );
          })}

          {/* ── 거래처 마커 — 크기 ∝ 금액 ── */}
          {points.map((c) => {
            const size = pinSize(c.amt) + (active(c.name) ? PIN_ACTIVE_BONUS : 0);
            return (
              <OverlayView key={c.name} position={{ lat: c.lat, lng: c.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                <Box onMouseEnter={() => setHover(c.name)} onMouseLeave={() => setHover(null)}
                  onClick={onItemClick ? () => onItemClick(c) : undefined}
                  sx={{ transform: "translate(-50%, -100%)", cursor: onItemClick ? "pointer" : "default" }}>
                  <PlaceIcon sx={{
                    fontSize: size,
                    color: ColorUtil.colorSet(c.colorIdx),
                    transition: "all 0.2s",
                    filter: active(c.name) ? "drop-shadow(0 2px 7px rgba(0,0,0,0.4))" : "none",
                  }} />
                  {active(c.name) && (
                    <Box sx={{
                      position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", mb: 0.5,
                      whiteSpace: "nowrap", bgcolor: "rgba(255,255,255,0.96)", border: 1, borderColor: "divider",
                      borderRadius: 1, px: 0.75, py: 0.25, boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    }}>
                      <Typography sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.primary" }}>{c.name}</Typography>
                      <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{fmtMoney(c.amt)}</Typography>
                      {c.qty != null && (
                        <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{fmtQty(c.qty)}</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </OverlayView>
            );
          })}
        </GoogleMap>
      )}
      </Box>
    </Card>
  );
};

export default WorldMapPanel;
